import React, { useState, useEffect } from 'react';

// Installers are hosted on the same origin under /downloads/ (Hostinger),
// preserved across frontend deploys via an rsync --exclude.
// The ?v= query busts browser cache whenever a new build is released — bump it
// each release so employees never get a stale cached installer.
const BASE = '/downloads';
const VER = '2.2.6';
const FILES = {
  macArm: `${BASE}/HRTrack-Agent-mac-arm64.dmg?v=${VER}`,
  macIntel: `${BASE}/HRTrack-Agent-mac-intel.dmg?v=${VER}`,
  win: `${BASE}/HRTrack-Agent-win.exe?v=${VER}`,
};

function detectOS() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  if (/Mac/i.test(platform) || /Mac OS X/i.test(ua)) return 'mac';
  return 'other';
}

const card = { background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 24, marginBottom: 16 };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none', cursor: 'pointer', border: 'none' };
const primaryBtn = { ...btn, background: '#4f46e5', color: '#fff' };
const outlineBtn = { ...btn, background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', color: 'var(--text-2)', border: '1px solid var(--glass-border)' };
const codeBox = { display: 'block', background: '#1f2937', color: '#a7f3d0', padding: '8px 10px', borderRadius: 6, fontSize: 12, margin: '5px 0 8px', wordBreak: 'break-all', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, Menlo, monospace' };

function AppleIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z"/></svg>;
}
function WindowsIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5.5L10.5 4.5v7H3v-6zM10.5 12.5v7L3 18.5v-6h7.5zM11.5 4.3L21 3v8.5h-9.5v-7.2zM21 12.5V21l-9.5-1.3v-7.2H21z"/></svg>;
}

export default function Downloads() {
  const [os, setOs] = useState('other');
  useEffect(() => { setOs(detectOS()); }, []);

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>Download Desktop App</h1>
      <p style={{ color: 'var(--muted)', margin: '0 0 24px', fontSize: 14 }}>
        Install the HRTrack agent to track time and capture screenshots automatically in the background.
        It runs quietly from your menu bar / system tray.
      </p>

      {/* macOS */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <AppleIcon />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>macOS</h2>
          {os === 'mac' && <span style={{ fontSize: 11, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', padding: '3px 8px', borderRadius: 12 }}>YOUR DEVICE</span>}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 14px' }}>
          Not sure which one? Apple menu → <strong>About This Mac</strong>. If the chip says
          "Apple M1/M2/M3…" pick <strong>Apple Silicon</strong>; if it says "Intel" pick <strong>Intel</strong>.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href={FILES.macArm} style={primaryBtn}><AppleIcon /> Apple Silicon (M1–M4)</a>
          <a href={FILES.macIntel} style={outlineBtn}><AppleIcon /> Intel Mac</a>
        </div>
      </div>

      {/* Windows */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <WindowsIcon />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Windows</h2>
          {os === 'windows' && <span style={{ fontSize: 11, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', padding: '3px 8px', borderRadius: 12 }}>YOUR DEVICE</span>}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href={FILES.win} style={primaryBtn}><WindowsIcon /> Download for Windows (.exe)</a>
        </div>
      </div>

      {/* First-launch setup instructions */}
      <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#92400e', margin: '0 0 8px' }}>⚠️ First-time setup</h3>
        <p style={{ color: '#78350f', fontSize: 13, margin: '0 0 14px' }}>
          The app isn't signed with a paid developer certificate yet, so your computer shows one-time prompts. This is expected — nothing is wrong with the app.
        </p>

        <p style={{ color: '#92400e', fontSize: 13, fontWeight: 700, margin: '0 0 4px' }}>Windows</p>
        <p style={{ color: '#78350f', fontSize: 13, margin: '0 0 16px' }}>
          Run the installer. If you see <em>"Windows protected your PC,"</em> click <strong>More info → Run anyway</strong>. That's all.
        </p>

        <p style={{ color: '#92400e', fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>macOS</p>
        <ol style={{ color: '#78350f', fontSize: 13, margin: 0, paddingLeft: 18, lineHeight: 1.65 }}>
          <li>Open the downloaded <strong>.dmg</strong> and drag <strong>HRTracking</strong> into your <strong>Applications</strong> folder.</li>
          <li style={{ marginTop: 6 }}>
            If macOS says the app is <em>"damaged"</em> or <em>"could not be verified,"</em> open <strong>Terminal</strong> (Spotlight → type "Terminal") and paste this, then press Enter:
            <code style={codeBox}>xattr -dr com.apple.quarantine /Applications/HRTracking.app</code>
            Now open <strong>HRTracking</strong> from Applications (right-click → Open the first time).
          </li>
          <li style={{ marginTop: 6 }}>
            When prompted, grant access in <strong>System Settings → Privacy &amp; Security</strong> — enable <strong>HRTracking</strong> under <em>both</em>:
            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
              <li><strong>Screen &amp; System Audio Recording</strong> — needed for screenshots</li>
              <li><strong>Accessibility</strong> — needed for keystroke &amp; click counts</li>
            </ul>
            Then <strong>quit and reopen</strong> the app — macOS only applies these after a restart.
          </li>
          <li style={{ marginTop: 6 }}>
            If a permission won't stick or the app keeps re-asking, reset it in <strong>Terminal</strong>, then reopen the app and grant it again:
            <code style={codeBox}>tccutil reset ScreenCapture com.hrtrack.desktop{'\n'}tccutil reset Accessibility com.hrtrack.desktop</code>
          </li>
        </ol>
        <p style={{ color: '#78350f', fontSize: 12, margin: '12px 0 0' }}>
          Stuck? Ask your IT/admin — a warning-free, notarized Mac build is on the way.
        </p>
      </div>

      {/* How it works */}
      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>After installing</h3>
        <ul style={{ color: 'var(--text-2)', fontSize: 13, margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Open the app — it appears as a small icon in your menu bar (Mac) or system tray (Windows).</li>
          <li>Click the icon → <strong>Settings</strong> → log in with your HRTrack email and password.</li>
          <li>It starts tracking automatically and launches on startup. Use the tray menu to start/stop anytime.</li>
        </ul>
      </div>
    </div>
  );
}
